import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { apiGet } from "../../../DataService/apiClient";
import { useUser } from "../../../DataService/UserContext";
import { AdminOrder, AdminOrdersResponse } from "../../../Types/AdminOrderTypes";
import { MemberHistoryResponse, MemberOrderHistory } from "../../../Types/MemberHistoryTypes";
import "../MemberDashboard.css";

type OrderDetailsItem = {
  id: string;
  title: string;
  itemType: string;
  quantity: number;
  lineAmount: number;
  programPaymentStage?: string | null;
  bookingMode?: string | null;
  includedSessionSource?: string | null;
};

type OrderDetailsModel = {
  id: string;
  orderNumber: string;
  status: string;
  createdAt: string;
  paymentMethod: string;
  paymentReference: string;
  totalAmount: number;
  items: OrderDetailsItem[];
};

const mapMemberOrder = (order: MemberOrderHistory): OrderDetailsModel => ({
  id: order.id,
  orderNumber: order.orderNumber,
  status: order.status,
  createdAt: order.createdAt,
  paymentMethod: order.paymentMethod,
  paymentReference: order.paymentReference,
  totalAmount: order.totalAmount,
  items: order.items,
});

const mapAdminOrder = (order: AdminOrder): OrderDetailsModel => ({
  id: order.id,
  orderNumber: order.orderNumber,
  status: order.status,
  createdAt: order.createdAt,
  paymentMethod: order.paymentMethod,
  paymentReference: order.paymentReference,
  totalAmount: order.totalAmount,
  items: order.items,
});

export const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const { user } = useUser();
  const [order, setOrder] = useState<OrderDetailsModel | null>(null);
  const [loading, setLoading] = useState(true);
  const supportOrder = (location.state as { order?: AdminOrder } | null | undefined)?.order;

  const getRotPaymentLabel = (stage?: string | null) => {
    if (!stage) return null;
    if (stage === 'registration') return 'ROT: registrerad';
    if (stage === 'balance') return 'ROT: fullt betald';
    return null;
  };

  useEffect(() => {
    const load = async () => {
      if (supportOrder) {
        setOrder(mapAdminOrder(supportOrder));
        setLoading(false);
        return;
      }

      try {
        const history = await apiGet<MemberHistoryResponse>("/Auth/me/history");
        const found = history.orders.find(x => x.id === orderId || x.orderNumber === orderId);
        if (found) {
          setOrder(mapMemberOrder(found));
          setLoading(false);
          return;
        }

        if (user?.roles.includes('admin')) {
          const adminOrders = await apiGet<AdminOrdersResponse>("/AdminOrders");
          const adminFound = adminOrders.orders.find(x => x.id === orderId || x.orderNumber === orderId);
          setOrder(adminFound ? mapAdminOrder(adminFound) : null);
          setLoading(false);
          return;
        }

        setOrder(null);
      } catch (err) {
        console.error("Order details load error", err);
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orderId, supportOrder, user]);

  if (loading) {
    return <div className="dashboard-section"><p>Laddar order...</p></div>;
  }

  if (!order) {
    return (
      <div className="dashboard-section">
        <div className="dashboard-card">
          <h1>Order hittades inte</h1>
          <p>Den här ordern finns inte i din historik.</p>
          <Link to="/member-home" className="dashboard-link-btn">Tillbaka</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-section">
      <div className="dashboard-header">
        <h1>Order {order.orderNumber}</h1>
        <p>{order.status} · {new Date(order.createdAt).toLocaleString()}</p>
      </div>

      <div className="dashboard-card">
        <div className="dashboard-order-meta-grid">
          <div><span>Betalning</span><strong>{order.paymentMethod}</strong></div>
          <div><span>Referens</span><strong>{order.paymentReference}</strong></div>
          <div><span>Totalt</span><strong>{order.totalAmount.toFixed(2)} SEK</strong></div>
        </div>
      </div>

      <div className="dashboard-card">
        <h3>Orderrader</h3>
        <div className="dashboard-history-list">
          {order.items.map(item => (
            <div key={item.id} className="dashboard-history-item">
              <div>
                <strong>{item.title}</strong>
                <p>{item.itemType} · Antal {item.quantity}</p>
                {getRotPaymentLabel(item.programPaymentStage) && (
                  <p>{getRotPaymentLabel(item.programPaymentStage)}</p>
                )}
                {item.bookingMode && (
                  <p>
                    Bokning: {item.bookingMode === 'intro'
                      ? 'introduktionssamtal'
                      : item.bookingMode === 'included'
                        ? `inkluderad från ${item.includedSessionSource ?? '-'}`
                        : 'betald session'}
                  </p>
                )}
              </div>
              <div className="dashboard-history-meta">
                <strong>{item.lineAmount.toFixed(2)} SEK</strong>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
